<script setup lang="ts">
/**
 * Liste « où ? » : « Utiliser ma position actuelle » + villes proposées.
 * Rendu UNIQUE partagé par la barre de recherche de l'en-tête et la fenêtre
 * « Filtres » (2026-09-26) — la logique vit dans `useRechercheVille`.
 */
import type { VilleSuggestion } from "~/composables/useRechercheVille";

defineProps<{
  requete: string;
  suggestions: VilleSuggestion[];
  enCours: boolean;
  geoEnCours: boolean;
  geoErreur: string | null;
}>();

const emit = defineEmits<{
  choisir: [ville: VilleSuggestion];
  "ma-position": [];
}>();
</script>

<template>
  <div>
    <!-- Ma position (géolocalisation + commune la plus proche) -->
    <button
      type="button"
      class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-main/5 hover:bg-main/10 transition-colors text-left mb-2"
      :disabled="geoEnCours"
      @click="emit('ma-position')"
    >
      <div class="size-9 rounded-full bg-main/10 flex items-center justify-center shrink-0">
        <UIcon v-if="!geoEnCours" name="i-lucide-locate-fixed" class="size-4 text-main" />
        <UIcon v-else name="i-lucide-loader-2" class="size-4 text-main animate-spin" />
      </div>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium text-main">
          {{ geoEnCours ? $t("association.recherche.localisationEnCours") : $t("association.recherche.maPosition") }}
        </div>
        <div v-if="geoErreur" class="text-xs text-red-600 mt-0.5">
          {{ geoErreur }}
        </div>
      </div>
    </button>

    <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 mt-4 px-2">
      <template v-if="requete">
        {{ enCours ? $t("association.recherche.rechercheEnCours") : $t("association.recherche.villesCorrespondant", { requete }) }}
      </template>
      <template v-else>{{ $t("association.recherche.villesAvecResultats") }}</template>
    </h3>

    <div v-if="suggestions.length > 0" class="flex flex-col">
      <button
        v-for="ville in suggestions"
        :key="`${ville.source}-${ville.slug}`"
        type="button"
        class="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left"
        @click="emit('choisir', ville)"
      >
        <div class="size-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
          <UIcon name="i-lucide-map-pin" class="size-4 text-gray-500" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-gray-900 truncate">{{ ville.name }}</div>
          <div v-if="ville.postalCode" class="text-xs text-gray-500">{{ ville.postalCode }}</div>
          <div v-else-if="ville.count != null" class="text-xs text-gray-500">
            {{ $t("association.recherche.nResultats", { n: ville.count }, ville.count) }}
          </div>
        </div>
      </button>
    </div>

    <p
      v-else-if="requete && !enCours"
      class="text-sm text-gray-500 italic px-3 py-4 text-center"
    >
      {{ $t("association.recherche.aucuneCommune", { requete }) }}
    </p>
    <p v-else-if="!requete" class="text-sm text-gray-400 italic px-3 py-4 text-center">
      {{ $t("association.recherche.tapezVille") }}
    </p>
  </div>
</template>
